import React, { useEffect, useState } from "react";
import ReportsModal from "../../../core/modals/reports_modal";
import { Link, useSearchParams } from "react-router-dom";
import { useLeadsData } from "../../crm/leads/hooks/useLeadsData";
import PageHeader from "../../../core/common/crmComponents/PageHeader";
import LeadList from "./components/LeadList";
import LoaderComponent from "../../../core/common/crmComponents/LoaderComponent";
import PaginationControls from "../../../core/common/pagination";
import CompanyFilter from "../../../core/common/crmComponents/CompanyFilter";
import Filter from "../common-components/Filter";
import api from "../../../utils/api";
import LeadsByYearBarChart from "./components/LeadsByYearBarChart";
import LeadsBySourceChart from "./components/LeadsBySourceChart";
import { useDispatch, useSelector } from "react-redux";
import { MODULES } from "../../../core/data/constants/moduleConstants";
import { generateQueryParams } from "../../../utils/helpers/generateQueryParams";
import CommonFilter from "../../../core/common/crmComponents/filters/CommonFilter";
import ClearAllFilters from "../../../core/common/crmComponents/filters/ClearAllFilters";
import ReloadButton from "../../../core/common/crmComponents/ReloadButton";
import queryClient from "../../../config/queryClient";
import { setReloading } from "../../../core/data/redux/moduleDataSlice";
import { useFunctionalityAccess } from "../../../core/common/wrapper/HasFunctionalityAccess";
import { toast } from "react-toastify";

const LeadReport = () => {
  const user = JSON.parse(localStorage.getItem("user") || []);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const reloading = useSelector((state) => state.moduleData.reloading);
  const hasDownloadAccess = useFunctionalityAccess("downloadReport");
  const [isDownloading, setIsDownloading] = useState(false);

  const [page, setPage] = useState(1); // Manage current page state
  const [limit, setLimit] = useState(25);

  const [layout, setLayout] = useState("list");
  const { companyNameArr } = useSelector((state) => state.auth);

  const [selectedCompany, setSelectedCompany] = useState(() => {
    const companyIdFromParams = searchParams.get("companyid");

    return companyIdFromParams
      ? companyIdFromParams
      : user?.ofCompany
      ? user?.ofCompany?._id
      : companyNameArr.length > 0
      ? companyNameArr[0].value
      : null;
  });

  const { filters } = useSelector((state) => state.filter);
  const leadsFilters = filters.leads || [];

  const { queryParams, params } = generateQueryParams(leadsFilters);

  const { data, isLoading, isError, error, isFetching } = useLeadsData({
    page,
    limit,
    ofCompany: selectedCompany,
    queryString: params.toString(),
    queryParams: queryParams.toString(),
  });

  const [reportDataFilter, setReportDataFilter] = useState({
    format: "excel",
    fromDate: "",
    toDate: "",
    filters: {
      ofCompany: selectedCompany,
      queryString: params.toString(),
      queryParams: queryParams.toString(),
    },
  });

  const leadsData = data?.data?.data;

  const totalCount = data?.data?.totalResult;

  const totalPages = Math.ceil(totalCount / limit);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update page state to trigger refetch
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset page to 1 whenever limit changes
  };

  useEffect(() => {
    setReportDataFilter((prev) => {
      const existingParams = new URLSearchParams(
        prev.filters?.queryParams || ""
      );
      const newParams = new URLSearchParams(queryParams.toString());

      // Preserve any additional keys (e.g., format, date range) from existingParams
      for (const [key, value] of existingParams.entries()) {
        if (!newParams.has(key)) {
          newParams.set(key, value);
        }
      }

      return {
        ...prev,
        filters: {
          ...prev.filters,
          ofCompany: selectedCompany,
          queryString: params.toString(),
          queryParams: newParams.toString(),
        },
      };
    });
  }, [selectedCompany, leadsFilters]);

  const handleDownload = async () => {
    const { fromDate, toDate, format, filters } = reportDataFilter;

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffInMonths =
      to.getMonth() -
      from.getMonth() +
      12 * (to.getFullYear() - from.getFullYear());

    if (diffInMonths > 3 || from > to) {
      toast.error("You can only download reports for a maximum of 3 months.");
      return;
    }
    setIsDownloading(true);
    try {
      const { queryString, queryParams, ...restParams } = filters;
      const response = await api.get(
        `/file-system/download-lead-report?${queryString}&${queryParams}&populate=ofCompany,assignedTo,lastUpdationBy&selectPopulate=name`,
        {
          params: {
            format,
            ...restParams,
            fields: "-timeline",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `leads-report.${format === "excel" ? "xlsx" : "csv"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download report.");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (leadsFilters?.length > 0) {
      setPage(1);
    }
  }, [leadsFilters]);

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["leads"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader
                pageName="Leads Report"
                totalCount={totalCount || 0}
              />
              <div className="card">
                <div className="card-header py-2">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                    {/* Left Section */}
                    <div className="d-flex flex-wrap gap-3">
                      <ReloadButton moduleName={MODULES.LEADS} />

                      {/* Company Filter */}
                      <CompanyFilter
                        selectedCompany={selectedCompany}
                        setSelectedCompany={setSelectedCompany}
                        user={user}
                      />
                    </div>

                    {/* Right Section */}
                    <div className="d-flex align-items-center gap-3">
                      <div className="gap-1 d-flex align-items-center flex-wrap">
                        <CommonFilter module={MODULES.LEADS} />
                        <ClearAllFilters module={MODULES.LEADS} />
                      </div>
                      {hasDownloadAccess && (
                        <button
                          className="btn btn-primary custom-btn-padding"
                          data-bs-toggle="modal"
                          data-bs-target="#download_report"
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm fs-5 me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-file-download fs-5 me-2" />
                              Download Report
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-body py-2">
                  <div className="row">
                    <LeadsByYearBarChart selectedCompany={selectedCompany} />
                    {/* <LeadsBySourceChart
                      user={user}
                      selectedCompany={selectedCompany}
                    /> */}
                  </div>

                  <PaginationControls
                    totalPages={totalPages}
                    totalCount={totalCount}
                    currentPageDataCount={Math.min(leadsData?.length, limit)}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    moduleName={MODULES.LEADS}
                  />

                  {isLoading ? (
                    <LoaderComponent />
                  ) : isError ? (
                    <div>Error: {error.message}</div>
                  ) : layout === "list" ? (
                    <LeadList leadsData={leadsData} />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* /Page Wrapper */}
      <ReportsModal
        handleDownload={handleDownload}
        reportDataFilter={reportDataFilter}
        setReportDataFilter={setReportDataFilter}
        module={MODULES.LEADS}
      />
    </div>
  );
};

export default LeadReport;
